import { combine, compute, dedupe } from '@exodus/atoms';
import { SynchronizedTime } from '@exodus/basic-utils';
import { FAIL_LIMIT as ORDER_STALE_TIME } from '@exodus/orders/monitor/fail-limit';
import { getOrdersInProgress } from '@exodus/orders/selectors/orders-in-progress';
import { resultFunction as getPotentialCompleteOrders } from '@exodus/orders/selectors/potential-complete-orders';
import lodash from 'lodash';
import { calculatePendingEvmReceives, makeOrderAssetAwaitingInboundTxIdKey, } from './pending-evm-receives.js';
import { cloneBalances } from './utils.js';
const { isEqual, debounce } = lodash;
const field = 'balance';
export const RECOMPUTE_DEBOUNCE_TIME = 400;
const isOrderFresh = (order) => {
    if (!order.date)
        return true;
    const orderTime = order.date instanceof Date ? order.date.getTime() : new Date(order.date).getTime();
    if (!Number.isFinite(orderTime))
        return true;
    return SynchronizedTime.now() - orderTime <= ORDER_STALE_TIME;
};
const dedupeReadonly = (atom) => dedupe(atom);
const buildOrderTxAndAssetAwaitingInboundGuards = (orders) => {
    const txIndex = new Map();
    const assetKeysAwaitingInboundTxId = new Set();
    for (const order of orders) {
        const { toAmount, toAsset, toWalletAccount, toTxId, potentialToTxIds } = order;
        if (!toAmount || !toAsset || !toWalletAccount)
            continue;
        const binding = { asset: toAsset, walletAccount: toWalletAccount };
        const txIds = [];
        if (toTxId)
            txIds.push(toTxId);
        if (potentialToTxIds && potentialToTxIds.length > 0)
            txIds.push(...potentialToTxIds);
        if (txIds.length === 0) {
            if (toAmount.isZero)
                continue;
            assetKeysAwaitingInboundTxId.add(makeOrderAssetAwaitingInboundTxIdKey(toWalletAccount, toAsset));
            continue;
        }
        for (const txId of txIds) {
            const bindings = txIndex.get(txId) ?? [];
            bindings.push(binding);
            txIndex.set(txId, bindings);
        }
    }
    return { txIndex, assetKeysAwaitingInboundTxId };
};
const buildFiatOrderTxIndex = (orders) => {
    const index = new Map();
    for (const order of orders) {
        const { txId, toAsset, toWalletAccount } = order;
        if (!txId || !toAsset || !toWalletAccount)
            continue;
        const bindings = index.get(txId) ?? [];
        bindings.push({ asset: toAsset, walletAccount: toWalletAccount });
        index.set(txId, bindings);
    }
    return index;
};
class OptimisticBalances {
    #assetsModule;
    #ordersAtom;
    #restoringAssetsAtom;
    #balancesAtom;
    #optimisticBalancesAtom;
    #syncedBalancesAtom;
    #txLogsAtom;
    #fiatOrdersAtom;
    #enabledWalletAccountsAtom;
    #multipleWalletAccountsEnabledAtom;
    #logger;
    #ordersInProgress = [];
    #potentialCompleteOrders = [];
    #orderTxIndex = new Map();
    #orderAssetKeysAwaitingInboundTxId = new Set();
    #fiatOrderTxIndex = new Map();
    #enabledWalletAccounts = [];
    #multipleWalletAccountsEnabled = false;
    #syncedBalances = Object.create(null);
    #balances = Object.create(null);
    #orderCorrections = Object.create(null);
    #pendingEvmReceives = Object.create(null);
    #oldOptimisticBalances = Object.create(null);
    #optimisticBalances = Object.create(null);
    #restoringAssets = Object.create(null);
    #txLogs = Object.create(null);
    #subscriptions = [];
    #loaded = false;
    #recompute;
    constructor({ assetsModule, balancesAtom, config = Object.create(null), optimisticBalancesAtom, logger, ordersAtom, txLogsAtom, restoringAssetsAtom, syncedBalancesAtom, fiatOrdersAtom, enabledWalletAccountsAtom, multipleWalletAccountsEnabledAtom, }) {
        this.#assetsModule = assetsModule;
        this.#balancesAtom = balancesAtom;
        this.#optimisticBalancesAtom = optimisticBalancesAtom;
        this.#txLogsAtom = txLogsAtom;
        this.#ordersAtom = ordersAtom;
        this.#restoringAssetsAtom = restoringAssetsAtom;
        this.#syncedBalancesAtom = syncedBalancesAtom;
        this.#fiatOrdersAtom = fiatOrdersAtom;
        this.#enabledWalletAccountsAtom = enabledWalletAccountsAtom;
        this.#multipleWalletAccountsEnabledAtom = multipleWalletAccountsEnabledAtom;
        this.#logger = logger;
        this.#recompute = debounce(() => {
            if (Object.keys(this.#balances).length === 0)
                return;
            this.#recomputeOrderCorrections();
            this.#recomputePendingEvmReceives();
            this.#recomputeOptimisticBalances();
        }, config.debounceTime ?? RECOMPUTE_DEBOUNCE_TIME, { maxWait: 3000 });
    }
    load = () => {
        if (this.#loaded)
            return;
        this.#loaded = true;
        this.#subscriptions.push(this.#ordersAtom.observe(async (orderSet) => {
            const newOrdersInProgress = [...getOrdersInProgress(orderSet)].map((order) => ({
                orderId: order.orderId,
                date: order.date,
                toAmount: order.toAmount,
                toAsset: order.toAsset,
                toWalletAccount: order.toWalletAccount,
                toTxId: order.toTxId,
                potentialToTxIds: order.potentialToTxIds,
            }));
            let hasChanges = false;
            if (!isEqual(this.#ordersInProgress, newOrdersInProgress)) {
                hasChanges = true;
                this.#ordersInProgress = newOrdersInProgress;
            }
            const newPotentialCompleteOrders = [
                ...getPotentialCompleteOrders(orderSet),
            ].map((order) => ({
                orderId: order.orderId,
                date: order.date,
                toAmount: order.toAmount,
                toAsset: order.toAsset,
                toWalletAccount: order.toWalletAccount,
                toTxId: order.toTxId,
                potentialToTxIds: order.potentialToTxIds,
            }));
            if (!isEqual(this.#potentialCompleteOrders, newPotentialCompleteOrders)) {
                hasChanges = true;
                this.#potentialCompleteOrders = newPotentialCompleteOrders;
            }
            if (hasChanges) {
                const guards = buildOrderTxAndAssetAwaitingInboundGuards([
                    ...newOrdersInProgress,
                    ...newPotentialCompleteOrders,
                ]);
                this.#orderTxIndex = guards.txIndex;
                this.#orderAssetKeysAwaitingInboundTxId = guards.assetKeysAwaitingInboundTxId;
                this.#recompute();
            }
        }), this.#restoringAssetsAtom.observe(async (restoringAssets) => {
            const prevIsRestoring = this.#getIsRestoring();
            this.#restoringAssets = restoringAssets;
            const isRestoring = this.#getIsRestoring();
            if (!prevIsRestoring && !isRestoring) {
                return;
            }
            this.#recompute();
        }), this.#syncedBalancesAtom.observe(async (syncedBalances) => {
            if (!this.#getIsRestoring()) {
                return;
            }
            this.#syncedBalances = syncedBalances;
            this.#recompute();
        }), this.#balancesAtom.observe(({ balances }) => {
            this.#balances = balances;
            this.#recompute();
        }), this.#txLogsAtom.observe(({ value }) => {
            this.#txLogs = value;
            this.#recompute();
        }));
        if (this.#fiatOrdersAtom) {
            const fiatOrderTxIndexAtom = dedupeReadonly(compute({
                atom: this.#fiatOrdersAtom,
                selector: (fiatOrderSet) => buildFiatOrderTxIndex(fiatOrderSet
                    ? [...fiatOrderSet]
                        .filter((order) => order.exodusStatus === 'in-progress' ||
                        order.exodusStatus === 'action-required')
                        .map((order) => ({
                        orderId: order.orderId,
                        txId: order.txId,
                        toAsset: order.toAsset,
                        toWalletAccount: order.toWalletAccount,
                    }))
                    : []),
            }));
            this.#subscriptions.push(fiatOrderTxIndexAtom.observe((index) => {
                this.#fiatOrderTxIndex = index;
                this.#recompute();
            }));
        }
        if (this.#enabledWalletAccountsAtom && this.#multipleWalletAccountsEnabledAtom) {
            const enabledWalletAccounts = dedupeReadonly(compute({
                atom: this.#enabledWalletAccountsAtom,
                selector: (walletAccounts) => Object.keys(walletAccounts),
            }));
            const multipleWalletAccountsEnabled = compute({
                atom: this.#multipleWalletAccountsEnabledAtom,
                selector: (enabled) => enabled === true,
            });
            this.#subscriptions.push(combine({ enabledWalletAccounts, multipleWalletAccountsEnabled }).observe((snapshot) => {
                this.#enabledWalletAccounts = snapshot.enabledWalletAccounts;
                this.#multipleWalletAccountsEnabled = snapshot.multipleWalletAccountsEnabled;
                this.#recompute();
            }));
        }
    };
    #getIsRestoring = () => Object.keys(this.#restoringAssets).length > 0;
    #getSyncedBalancesAssetNames = (walletAccount) => {
        if (!this.#getIsRestoring()) {
            return [];
        }
        const syncedBalances = this.#syncedBalances[walletAccount];
        if (!syncedBalances) {
            return [];
        }
        const assetNames = [];
        for (const assetName in syncedBalances) {
            const balance = syncedBalances[assetName];
            if (!balance || balance.isZero)
                continue;
            const asset = this.#assetsModule.getAsset(assetName);
            if (!asset)
                continue;
            assetNames.push(assetName);
        }
        return assetNames;
    };
    #recomputeOptimisticBalances = () => {
        const balances = this.#balances;
        const oldOptimisticBalances = this.#oldOptimisticBalances;
        const corrections = this.#orderCorrections;
        const pendingEvmReceives = this.#pendingEvmReceives;
        const changes = Object.create(null);
        const optimisticBalances = Object.create(null);
        for (const walletAccount in balances) {
            const walletData = balances[walletAccount];
            const walletCorrections = corrections[walletAccount] ?? Object.create(null);
            const walletPendingEvmReceives = pendingEvmReceives[walletAccount] ?? Object.create(null);
            const assetNamesSet = Object.create(null);
            for (const assetName in walletData)
                assetNamesSet[assetName] = true;
            for (const assetName in walletCorrections)
                assetNamesSet[assetName] = true;
            for (const assetName in walletPendingEvmReceives)
                assetNamesSet[assetName] = true;
            const syncedBalancesAssetNames = this.#getSyncedBalancesAssetNames(walletAccount);
            for (const assetName of syncedBalancesAssetNames)
                assetNamesSet[assetName] = true;
            const walletOptimisticBalances = Object.create(null);
            const oldWalletOptimisticBalances = oldOptimisticBalances[walletAccount];
            const walletChanges = Object.create(null);
            for (const assetName in assetNamesSet) {
                const asset = this.#assetsModule.getAsset(assetName);
                if (!asset)
                    continue;
                const zero = asset.currency.ZERO;
                const oldBalance = oldWalletOptimisticBalances?.[assetName]?.[field];
                const balance = walletData[assetName]?.[field] ?? zero;
                const correction = walletCorrections[assetName];
                const settled = correction?.settled ?? zero;
                const expected = correction?.expected ?? zero;
                const pendingEvmReceive = walletPendingEvmReceives[assetName] ?? zero;
                const isRestoring = this.#restoringAssets[asset.baseAsset.name];
                const syncedBalance = this.#syncedBalances[walletAccount]?.[assetName];
                const isAccountStateAsset = asset.baseAsset.api?.features?.accountState;
                let newBalance;
                if (isRestoring && syncedBalance && !isAccountStateAsset) {
                    newBalance = syncedBalance;
                }
                else if (isRestoring) {
                    newBalance = balance;
                }
                else if (asset.baseAsset.api?.features?.noHistory) {
                    newBalance = balance;
                }
                else {
                    const adjustedSettled = balance.lt(settled) ? balance : settled;
                    newBalance = balance.sub(adjustedSettled).add(expected).add(pendingEvmReceive);
                }
                if (newBalance.isNegative) {
                    newBalance = balance;
                }
                else if (!oldBalance || !oldBalance.equals(newBalance)) {
                    walletChanges[assetName] = { balance: { from: oldBalance || zero, to: newBalance } };
                }
                walletOptimisticBalances[assetName] = { [field]: newBalance };
            }
            if (Object.keys(walletChanges).length > 0) {
                changes[walletAccount] = walletChanges;
            }
            optimisticBalances[walletAccount] = walletOptimisticBalances;
        }
        this.#optimisticBalances = optimisticBalances;
        if (Object.keys(changes).length > 0) {
            this.#flush({ changes });
        }
    };
    #recomputeOrderCorrections = () => {
        if (this.#ordersInProgress.length === 0 && this.#potentialCompleteOrders.length === 0) {
            this.#orderCorrections = Object.create(null);
            return;
        }
        const result = Object.create(null);
        const usedTxKeys = new Set();
        const makeUsedTxKey = (walletAccount, assetName, txId) => `${walletAccount}\u0000${assetName}\u0000${txId}`;
        const addCorrection = ({ walletAccount, assetName, expected, settled, zero, }) => {
            const wallet = result[walletAccount] ?? (result[walletAccount] = Object.create(null));
            const slot = wallet[assetName] ?? (wallet[assetName] = { settled: zero, expected: zero });
            slot.settled = slot.settled.add(settled);
            slot.expected = slot.expected.add(expected);
        };
        const sumMatchedTxs = ({ walletAccount, assetName, txIds, txLog, zero, }) => {
            if (!txLog)
                return zero;
            let total = zero;
            for (const txId of txIds) {
                if (!txId)
                    continue;
                const usedKey = makeUsedTxKey(walletAccount, assetName, txId);
                if (usedTxKeys.has(usedKey))
                    continue;
                const tx = txLog.get(txId);
                if (!tx || tx.failed || !tx.coinAmount)
                    continue;
                usedTxKeys.add(usedKey);
                total = total.add(tx.coinAmount);
            }
            return total;
        };
        const groups = [this.#potentialCompleteOrders, this.#ordersInProgress];
        for (const orders of groups) {
            for (const order of orders) {
                try {
                    if (!isOrderFresh(order))
                        continue;
                    const { toAsset, toAmount, toWalletAccount, toTxId, potentialToTxIds } = order;
                    if (!toAmount || !toAsset || !toWalletAccount)
                        continue;
                    const asset = this.#assetsModule.getAsset(toAsset);
                    if (!asset)
                        continue;
                    const zero = asset.currency.ZERO;
                    const txLog = this.#txLogs[toWalletAccount]?.[toAsset];
                    const explicitTxIds = [];
                    if (toTxId)
                        explicitTxIds.push(toTxId);
                    if (potentialToTxIds && potentialToTxIds.length > 0) {
                        for (const txId of potentialToTxIds)
                            explicitTxIds.push(txId);
                    }
                    const settled = sumMatchedTxs({
                        walletAccount: toWalletAccount,
                        assetName: toAsset,
                        txIds: explicitTxIds,
                        txLog,
                        zero,
                    });
                    addCorrection({
                        walletAccount: toWalletAccount,
                        assetName: toAsset,
                        expected: toAmount,
                        settled,
                        zero,
                    });
                }
                catch (error) {
                    this.#logger.warn('[optimistic-balances] Failed to calculate incoming order balance, skipping order', {
                        orderId: order.orderId,
                        toAsset: order.toAsset,
                        toWalletAccount: order.toWalletAccount,
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        }
        this.#orderCorrections = result;
    };
    #recomputePendingEvmReceives = () => {
        try {
            this.#pendingEvmReceives = calculatePendingEvmReceives({
                txLogs: this.#txLogs,
                orderTxIndex: this.#orderTxIndex,
                orderAssetKeysAwaitingInboundTxId: this.#orderAssetKeysAwaitingInboundTxId,
                fiatOrderTxIndex: this.#fiatOrderTxIndex,
                assetsModule: this.#assetsModule,
                enabledWalletAccounts: this.#enabledWalletAccounts,
                multipleWalletAccountsEnabled: this.#multipleWalletAccountsEnabled,
                onError: ({ walletAccount, assetName, error }) => this.#logger.warn('[optimistic-balances] Failed to calculate pending EVM receive for asset, skipping asset', {
                    walletAccount,
                    assetName,
                    error: error instanceof Error ? error.message : String(error),
                    stack: error instanceof Error ? error.stack : undefined,
                }),
            });
        }
        catch (error) {
            this.#logger.warn('[optimistic-balances] Failed to compute pending EVM receives (outer)', {
                error: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
            });
        }
    };
    #flush = ({ changes } = {}) => {
        this.#logger.log('optimistic balances updated');
        const balances = cloneBalances(this.#optimisticBalances);
        void this.#optimisticBalancesAtom.set({ balances, changes });
        this.#oldOptimisticBalances = balances;
    };
    stop = () => {
        this.#recompute.cancel();
        this.#subscriptions.forEach((unsubscribe) => unsubscribe());
        this.#subscriptions = [];
        this.#loaded = false;
    };
}
const createOptimisticBalances = (opts) => new OptimisticBalances(opts);
const optimisticBalancesDefinition = {
    id: 'optimisticBalances',
    type: 'module',
    factory: createOptimisticBalances,
    dependencies: [
        'assetsModule',
        'balancesAtom',
        'optimisticBalancesAtom',
        'logger',
        'ordersAtom',
        'txLogsAtom',
        'restoringAssetsAtom',
        'syncedBalancesAtom',
        'fiatOrdersAtom?',
        'enabledWalletAccountsAtom?',
        'multipleWalletAccountsEnabledAtom?',
        'config?',
    ],
    public: true,
};
export default optimisticBalancesDefinition;
